import * as React from 'react';
import { SEARCH_DEBOUNCE_MS } from '../data-grid.constants';

interface IUseSearchInputInput {
	onSearch?: (query: string) => void;
	/** The grid's committed search term. */
	searchQuery?: string;
}

export interface IUseSearchInputResult {
	query: string;
	onChange: (query: string) => void;
	onSubmit: () => void;
}

/**
 * Owns the search box's text while debouncing what reaches the grid.
 *
 * Typing wins over the committed term so keystrokes are never yanked back mid-word, but an
 * incoming term the user did not type cancels any pending keystroke and takes the box over.
 * Without that, a debounce landing afterwards would silently re-apply the abandoned search.
 * Back/forward never reaches here: it remounts the grid, which drops any pending keystroke.
 */
export function useSearchInput({
	onSearch,
	searchQuery,
}: IUseSearchInputInput): IUseSearchInputResult {
	const [query, setQuery] = React.useState(searchQuery ?? '');
	const emittedQueryRef = React.useRef(searchQuery ?? '');
	const pendingEmitRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const cancelPendingEmit = React.useCallback(() => {
		if (pendingEmitRef.current == null) {
			return;
		}
		clearTimeout(pendingEmitRef.current);
		pendingEmitRef.current = undefined;
	}, []);

	const emit = React.useCallback(
		(next: string) => {
			emittedQueryRef.current = next;
			onSearch?.(next);
		},
		[onSearch]
	);

	const onChange = React.useCallback(
		(next: string) => {
			setQuery(next);
			cancelPendingEmit();
			pendingEmitRef.current = setTimeout(() => {
				pendingEmitRef.current = undefined;
				emit(next);
			}, SEARCH_DEBOUNCE_MS);
		},
		[cancelPendingEmit, emit]
	);

	const onSubmit = React.useCallback(() => {
		cancelPendingEmit();
		emit(query);
	}, [cancelPendingEmit, emit, query]);

	React.useEffect(() => {
		const incoming = searchQuery ?? '';
		if (incoming === emittedQueryRef.current) {
			return;
		}
		emittedQueryRef.current = incoming;
		cancelPendingEmit();
		setQuery(incoming);
	}, [cancelPendingEmit, searchQuery]);

	React.useEffect(() => cancelPendingEmit, [cancelPendingEmit]);

	return { query, onChange, onSubmit };
}
